// Real-time connection (Phase 15): one WebSocket per signed-in browser tab to the backend's /ws.
// The backend forwards engine events (agent.state, call.*, queue.updated, campaign.updated,
// contact.updated, disposition.required, dialer.updated) already filtered for this user's role.
// Pages subscribe with useLiveRefresh(); while the connection is live they poll less often
// (usePoll), and fall back to normal polling when it is down.

import { create } from 'zustand';

export interface LiveEvent { event: string; tenant_id: number; data: Record<string, any>; at: string }
export type LiveStatus = 'off' | 'connecting' | 'live' | 'engine-down' | 'reconnecting';

type Listener = (e: LiveEvent) => void;

interface RealtimeState {
  status: LiveStatus;
  recent: LiveEvent[];           // newest first, last 50
  received: number;
  connect: (token: string) => void;
  disconnect: () => void;
}

let ws: WebSocket | null = null;
let wanted: string | null = null;      // token we should be connected with
let retries = 0;
let retryTimer: ReturnType<typeof setTimeout> | null = null;
let pingTimer: ReturnType<typeof setInterval> | null = null;
const listeners = new Set<Listener>();

export function onLiveEvent(fn: Listener): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

function wsUrl(token: string): string {
  const proto = location.protocol === 'https:' ? 'wss' : 'ws';
  return `${proto}://${location.host}/ws?token=${encodeURIComponent(token)}`;
}

export const useRealtime = create<RealtimeState>((set, get) => {
  function open(token: string) {
    set({ status: retries ? 'reconnecting' : 'connecting' });
    const sock = new WebSocket(wsUrl(token));
    ws = sock;
    sock.onmessage = (m) => {
      let f: any;
      try { f = JSON.parse(String(m.data)); } catch { return; }
      if (f.type === 'hello') { retries = 0; set({ status: f.engine === 'up' ? 'live' : 'engine-down' }); }
      else if (f.type === 'engine') set({ status: f.status === 'up' ? 'live' : 'engine-down' });
      else if (f.type === 'event') {
        const e: LiveEvent = { event: f.event, tenant_id: f.tenant_id, data: f.data ?? {}, at: f.at };
        set((s) => ({ recent: [e, ...s.recent].slice(0, 50), received: s.received + 1 }));
        for (const l of listeners) { try { l(e); } catch { /* a page's handler must not break the feed */ } }
      }
    };
    sock.onclose = () => {
      if (ws !== sock) return;
      ws = null;
      if (!wanted) { set({ status: 'off' }); return; }
      set({ status: 'reconnecting' });
      const delay = Math.min(30000, 1000 * 2 ** Math.min(retries++, 5));
      retryTimer = setTimeout(() => { retryTimer = null; if (wanted) open(wanted); }, delay);
    };
  }

  return {
    status: 'off', recent: [], received: 0,
    connect(token) {
      if (wanted === token && ws) return;
      get().disconnect();
      wanted = token;
      retries = 0;
      open(token);
      pingTimer = setInterval(() => { if (ws?.readyState === WebSocket.OPEN) ws.send('{"type":"ping"}'); }, 20000);
    },
    disconnect() {
      wanted = null;
      if (retryTimer) { clearTimeout(retryTimer); retryTimer = null; }
      if (pingTimer) { clearInterval(pingTimer); pingTimer = null; }
      const s = ws;
      ws = null;
      s?.close();
      set({ status: 'off', recent: [] });
    },
  };
});
